import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import {
  Sparkles, Loader2, Plus, ChevronLeft, ChevronRight, Calendar,
  CheckCircle, Circle, Clock, AlertTriangle, Trash2, Edit3,
  Download, ZoomIn, ZoomOut, Milestone, Flag, X, Save
} from "lucide-react";

const COLORS = ["#36A9E1", "#E8A838", "#4ADE80", "#F87171", "#A78BFA", "#F472B6", "#38BDF8", "#FBBF24"];
const STATUS_MAP = {
  "not-started": { label: "Not Started", color: "#8B95A5", icon: Circle },
  "in-progress": { label: "In Progress", color: "#36A9E1", icon: Clock },
  "completed": { label: "Completed", color: "#4ADE80", icon: CheckCircle },
  "at-risk": { label: "At Risk", color: "#F87171", icon: AlertTriangle },
  "milestone": { label: "Milestone", color: "#E8A838", icon: Flag },
};

const GanttChartPage = () => {
  const { t } = useTranslation('operations');
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [viewWeeks, setViewWeeks] = useState(16);
  const [startOffset, setStartOffset] = useState(0);
  const [editTask, setEditTask] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: "", sector: "life-sciences", description: "", duration: "6 months" });
  const scrollRef = useRef(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/gantt/projects`, { withCredentials: true });
      if (res.data.length > 0) {
        setProject(res.data[0]);
        setTasks(res.data[0].tasks || []);
      }
    } catch (err) { console.error(err); }
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/gantt/generate`, projectForm, { withCredentials: true });
      setProject(res.data);
      setTasks(res.data.tasks || []);
    } catch (err) { console.error(err); }
    finally { setGenerating(false); }
  };

  const saveTask = async (task) => {
    try {
      await axios.put(`${API}/gantt/tasks/${project.project_id}`, task, { withCredentials: true });
      setTasks(prev => prev.map(t => t.id === task.id ? task : t));
    } catch (err) { console.error(err); }
  };

  const addTask = (task) => {
    const newTask = { ...task, id: `T-${Date.now()}`, color: COLORS[tasks.length % COLORS.length] };
    const updated = [...tasks, newTask];
    setTasks(updated);
    if (project) {
      axios.put(`${API}/gantt/tasks/${project.project_id}`, { tasks: updated }, { withCredentials: true }).catch(console.error);
    }
    setShowAdd(false);
  };

  const deleteTask = (taskId) => {
    const updated = tasks.filter(t => t.id !== taskId);
    setTasks(updated);
    if (project) {
      axios.put(`${API}/gantt/tasks/${project.project_id}`, { tasks: updated }, { withCredentials: true }).catch(console.error);
    }
  };

  const toggleStatus = (taskId) => {
    const order = ["not-started", "in-progress", "completed", "at-risk"];
    setTasks(prev => prev.map(item => {
      if (item.id !== taskId) return item;
      const next = order[(order.indexOf(item.status) + 1) % order.length];
      const updated = { ...item, status: next };
      saveTask(updated);
      return updated;
    }));
  };

  const getWeekDates = () => {
    const start = new Date();
    start.setDate(start.getDate() + startOffset * 7);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: viewWeeks }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i * 7);
      return d;
    });
  };

  const getTaskPosition = (task, weeks) => {
    if (!task.startWeek && task.startWeek !== 0) return { left: 0, width: 0 };
    const startW = Math.max(0, task.startWeek - startOffset);
    const endW = Math.min(viewWeeks, (task.startWeek + task.duration) - startOffset);
    if (endW <= 0 || startW >= viewWeeks) return { left: 0, width: 0, hidden: true };
    const colW = 100 / viewWeeks;
    return { left: `${startW * colW}%`, width: `${(endW - startW) * colW}%` };
  };

  const exportGantt = () => {
    if (!project) return;
    let md = `# ${project.name} — Gantt Chart\n\n`;
    md += `Sector: ${project.sector}\nGenerated: ${new Date().toLocaleDateString()}\n\n`;
    md += `| Task | Status | Start Week | Duration | Dependencies |\n|------|--------|-----------|----------|-------------|\n`;
    tasks.forEach(t => {
      md += `| ${t.name} | ${STATUS_MAP[t.status]?.label} | W${t.startWeek + 1} | ${t.duration}w | ${t.dependencies?.join(", ") || "-"} |\n`;
    });
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `${project.name.replace(/\s/g, "_")}_gantt.md`; a.click();
    URL.revokeObjectURL(url);
  };

  const weeks = getWeekDates();

  // ============ NEW PROJECT VIEW ============
  if (!project && !generating) {
    return (
      <QMSLayout title={t('gantt.title')}>
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h2 className="font-['Syne'] text-2xl font-bold text-white mb-2">AI Project Timeline</h2>
            <p className="text-[#8B95A5]">Generate an intelligent Gantt chart with tasks, milestones, and dependencies based on your project requirements</p>
          </div>
          <div className="qms-card">
            <div className="qms-card-body space-y-4">
              <div>
                <label className="block text-[#CBD5E1] text-sm mb-1.5">Project Name</label>
                <input type="text" value={projectForm.name} onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))} className="qms-input" placeholder="e.g., ISO 13485 Implementation" data-testid="gantt-project-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Sector</label>
                  <select value={projectForm.sector} onChange={e => setProjectForm(p => ({ ...p, sector: e.target.value }))} className="qms-input" data-testid="gantt-sector">
                    <option value="life-sciences">Life Sciences</option>
                    <option value="medical-devices">Medical Devices</option>
                    <option value="automotive">Automotive</option>
                    <option value="aerospace">Aerospace</option>
                    <option value="industrial">Industrial</option>
                    <option value="iot">IoT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Duration</label>
                  <select value={projectForm.duration} onChange={e => setProjectForm(p => ({ ...p, duration: e.target.value }))} className="qms-input" data-testid="gantt-duration">
                    <option value="3 months">3 months</option>
                    <option value="6 months">6 months</option>
                    <option value="9 months">9 months</option>
                    <option value="12 months">12 months</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[#CBD5E1] text-sm mb-1.5">Project Description</label>
                <textarea value={projectForm.description} onChange={e => setProjectForm(p => ({ ...p, description: e.target.value }))} className="qms-input h-24 resize-none" placeholder="Describe key objectives, scope, standards..." data-testid="gantt-description" />
              </div>
              <button onClick={generateWithAI} disabled={!projectForm.name} className="btn-primary w-full py-3 justify-center disabled:opacity-40" data-testid="gantt-generate-btn">
                <Sparkles size={18} /> Generate AI Gantt Chart
              </button>
            </div>
          </div>
        </div>
      </QMSLayout>
    );
  }

  if (generating) {
    return (
      <QMSLayout title={t('gantt.title')}>
        <div className="flex flex-col items-center justify-center py-24">
          <Loader2 size={48} className="text-[#E8A838] animate-spin mb-6" />
          <h3 className="font-['Syne'] text-xl font-bold text-white mb-2">Generating Project Timeline</h3>
          <p className="text-[#8B95A5] text-center max-w-md">AI is creating tasks, milestones, and dependencies for {projectForm.name}...</p>
        </div>
      </QMSLayout>
    );
  }

  // ============ GANTT CHART VIEW ============
  return (
    <QMSLayout title={t('operations:gantt.title')}>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-['Syne'] text-lg font-bold text-white">{project?.name}</h2>
          <span className="text-[#8B95A5] text-xs">{tasks.length} tasks &middot; {tasks.filter(t => t.status === "completed").length} completed</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAdd(true)} className="btn-primary py-1.5 px-3 text-xs" data-testid="gantt-add-task">
            <Plus size={14} /> Add Task
          </button>
          <button onClick={exportGantt} className="btn-ghost py-1.5 px-3 text-xs" data-testid="gantt-export">
            <Download size={14} /> Export
          </button>
          <button onClick={() => { setProject(null); setTasks([]); }} className="btn-ghost py-1.5 px-3 text-xs">
            New Project
          </button>
        </div>
      </div>

      {/* Timeline Controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setStartOffset(o => o - 4)} className="p-1.5 text-[#8B95A5] hover:text-white bg-[#1F222D] rounded-[3px]"><ChevronLeft size={14} /></button>
          <span className="text-[#CBD5E1] text-xs font-medium">
            {weeks[0]?.toLocaleDateString("en", { month: "short", year: "numeric" })} — {weeks[weeks.length - 1]?.toLocaleDateString("en", { month: "short", year: "numeric" })}
          </span>
          <button onClick={() => setStartOffset(o => o + 4)} className="p-1.5 text-[#8B95A5] hover:text-white bg-[#1F222D] rounded-[3px]"><ChevronRight size={14} /></button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setViewWeeks(w => Math.max(8, w - 4))} className="p-1.5 text-[#8B95A5] hover:text-white bg-[#1F222D] rounded-[3px]"><ZoomIn size={14} /></button>
          <span className="text-[#8B95A5] text-xs mx-1">{viewWeeks}w</span>
          <button onClick={() => setViewWeeks(w => Math.min(52, w + 4))} className="p-1.5 text-[#8B95A5] hover:text-white bg-[#1F222D] rounded-[3px]"><ZoomOut size={14} /></button>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="qms-card overflow-hidden" data-testid="gantt-chart">
        {/* Header Row */}
        <div className="flex border-b border-[rgba(255,255,255,0.06)]">
          <div className="w-[280px] flex-shrink-0 p-2 text-[#8B95A5] text-[10px] uppercase tracking-wider font-medium border-r border-[rgba(255,255,255,0.06)]">
            Task
          </div>
          <div className="flex-1 flex" ref={scrollRef}>
            {weeks.map((w, i) => (
              <div key={i} className="flex-1 min-w-0 p-2 text-center border-r border-[rgba(255,255,255,0.03)] last:border-r-0">
                <div className="text-[#8B95A5] text-[9px]">{w.toLocaleDateString("en", { month: "short", day: "numeric" })}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Task Rows */}
        {tasks.map((task, idx) => {
          const pos = getTaskPosition(task, weeks);
          const st = STATUS_MAP[task.status] || STATUS_MAP["not-started"];
          return (
            <div key={task.id} className="flex border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.01)] group" data-testid={`gantt-task-${idx}`}>
              {/* Task Info */}
              <div className="w-[280px] flex-shrink-0 p-2 border-r border-[rgba(255,255,255,0.06)] flex items-center gap-2">
                <button onClick={() => toggleStatus(task.id)} title={st.label}>
                  <st.icon size={14} style={{ color: st.color }} />
                </button>
                <div className="flex-1 min-w-0">
                  <span className="text-[#CBD5E1] text-xs block truncate">{task.name}</span>
                  {task.type === "milestone" && <span className="text-[9px] text-[#E8A838]">MILESTONE</span>}
                </div>
                <button onClick={() => deleteTask(task.id)} className="opacity-0 group-hover:opacity-100 text-[#F87171] transition-opacity" data-testid={`gantt-delete-${idx}`}>
                  <Trash2 size={12} />
                </button>
              </div>
              {/* Bar */}
              <div className="flex-1 relative h-10">
                {/* Grid lines */}
                {weeks.map((_, i) => (
                  <div key={i} className="absolute top-0 bottom-0 border-r border-[rgba(255,255,255,0.02)]" style={{ left: `${(i / viewWeeks) * 100}%`, width: `${100 / viewWeeks}%` }} />
                ))}
                {/* Task bar */}
                {!pos.hidden && (
                  <div
                    className="absolute top-2 h-6 rounded-[2px] flex items-center px-2 transition-all cursor-pointer"
                    style={{
                      left: pos.left,
                      width: pos.width,
                      backgroundColor: task.status === "completed" ? `${task.color || "#36A9E1"}80` : (task.color || "#36A9E1"),
                      opacity: task.status === "completed" ? 0.6 : 0.85,
                    }}
                    title={`${task.name} — ${task.duration}w`}
                  >
                    <span className="text-white text-[9px] truncate font-medium">{task.name}</span>
                  </div>
                )}
                {/* Dependencies */}
                {task.dependencies?.map(depId => {
                  const dep = tasks.find(t => t.id === depId);
                  if (!dep) return null;
                  const depEnd = dep.startWeek + dep.duration - startOffset;
                  const taskStart = task.startWeek - startOffset;
                  if (depEnd < 0 || taskStart < 0) return null;
                  const colW = 100 / viewWeeks;
                  return (
                    <svg key={depId} className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ overflow: "visible" }}>
                      <line
                        x1={`${depEnd * colW}%`} y1="50%"
                        x2={`${taskStart * colW}%`} y2="50%"
                        stroke="#8B95A580" strokeWidth="1" strokeDasharray="3,3"
                      />
                    </svg>
                  );
                })}
              </div>
            </div>
          );
        })}

        {tasks.length === 0 && (
          <div className="p-8 text-center text-[#8B95A5] text-sm">No tasks yet. Click "Add Task" or generate with AI.</div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 text-xs">
        {Object.entries(STATUS_MAP).map(([key, val]) => (
          <span key={key} className="flex items-center gap-1.5" style={{ color: val.color }}>
            <val.icon size={12} /> {val.label}
          </span>
        ))}
      </div>

      {/* Add Task Modal */}
      {showAdd && <AddTaskModal onAdd={addTask} onClose={() => setShowAdd(false)} tasks={tasks} />}
    </QMSLayout>
  );
};

const AddTaskModal = ({ onAdd, onClose, tasks }) => {
  const [form, setForm] = useState({ name: "", startWeek: 0, duration: 2, status: "not-started", type: "task", dependencies: [], color: COLORS[tasks.length % COLORS.length] });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-[3px] w-[400px] p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-['Syne'] font-semibold text-white">Add Task</h3>
          <button onClick={onClose} className="text-[#8B95A5] hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[#CBD5E1] text-xs mb-1">Task Name</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="qms-input text-sm" placeholder="e.g., Gap Analysis" data-testid="add-task-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[#CBD5E1] text-xs mb-1">Start Week</label>
              <input type="number" value={form.startWeek} onChange={e => setForm(p => ({ ...p, startWeek: parseInt(e.target.value) || 0 }))} className="qms-input text-sm" min="0" data-testid="add-task-start" />
            </div>
            <div>
              <label className="block text-[#CBD5E1] text-xs mb-1">Duration (weeks)</label>
              <input type="number" value={form.duration} onChange={e => setForm(p => ({ ...p, duration: parseInt(e.target.value) || 1 }))} className="qms-input text-sm" min="1" data-testid="add-task-duration" />
            </div>
          </div>
          <div>
            <label className="block text-[#CBD5E1] text-xs mb-1">Type</label>
            <div className="flex gap-2">
              {["task", "milestone"].map(item => (
                <button key={item} onClick={() => setForm(p => ({ ...p, type: item, status: item === "milestone" ? "milestone" : p.status }))} className={`px-3 py-1.5 rounded-[3px] text-xs capitalize ${form.type === item ? "bg-[#36A9E1] bg-opacity-15 text-[#38BDF8] border border-[#36A9E1]" : "bg-[#181B23] text-[#8B95A5] border border-[rgba(255,255,255,0.06)]"}`}>{item}</button>
              ))}
            </div>
          </div>
          <button onClick={() => form.name && onAdd(form)} disabled={!form.name} className="btn-primary w-full py-2 justify-center disabled:opacity-40 text-sm" data-testid="add-task-submit">
            <Plus size={14} /> Add Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default GanttChartPage;
